import React from 'react';
import {
  Table as UITable,
  TableBody,
  TableRow,
  TableCell,
} from '../../../components/ui/table';
import { SeriesTable } from './seriesTable';
import { SeriesData } from '@/types/types';

/** Known 2F47-derived fields when full PrivateTag2F47 is not returned */
export type PrivateTagFallback = {
  StudyCondition?: string | null;
  HumanSubjectId?: string | null;
};

interface TableExpandedRowProps {
  colSpan: number;
  isLoading: boolean;
  error?: string;
  series?: SeriesData[];
  /** Full 2F47 private tag content (raw JSON or text) from query */
  privateTag2F47?: string | null;
  /** Fallback when privateTag2F47 is empty (e.g. API doesn't return it yet) */
  privateTagFallback?: PrivateTagFallback;
  className?: string;
}

function getKeyValueEntries(
  raw: string | null | undefined,
  fallback?: PrivateTagFallback
): { key: string; value: string }[] {
  const trimmed = raw?.trim();
  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return Object.entries(parsed).map(([k, v]) => ({
          key: k,
          value: v == null ? '' : String(v),
        }));
      }
    } catch {
      // not JSON, try fallback below
    }
  }
  if (fallback) {
    const entries: { key: string; value: string }[] = [];
    if (fallback.StudyCondition != null && fallback.StudyCondition !== '')
      entries.push({ key: 'studyCondition', value: fallback.StudyCondition });
    if (fallback.HumanSubjectId != null && fallback.HumanSubjectId !== '')
      entries.push({ key: 'HumanSubjectId', value: fallback.HumanSubjectId });
    if (entries.length) return entries;
  }
  return [];
}

function PrivateTagSection({
  content,
  fallback,
}: {
  content: string | null | undefined;
  fallback?: PrivateTagFallback;
}) {
  const entries = getKeyValueEntries(content, fallback);

  const rowPairs: { key: string; value: string }[][] = [];
  for (let i = 0; i < entries.length; i += 2) {
    rowPairs.push(entries.slice(i, i + 2));
  }

  return (
    <div className="p-4 pb-2">
      <div className="mb-2 text-sm font-semibold text-foreground">
        Upload Private tag values
      </div>
      {entries.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          No private tag (2F47) values for this study.
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-background">
          <UITable>
            <TableBody>
              {rowPairs.map((pair, idx) => {
                const first = pair[0];
                const second = pair[1];
                return (
                  <TableRow key={`${first.key}-${idx}`}>
                    <TableCell className="border border-border py-2 text-sm font-medium text-foreground">
                      {first.key}
                    </TableCell>
                    <TableCell className="border border-border py-2 text-sm text-foreground">
                      {first.value}
                    </TableCell>
                    {second ? (
                      <>
                        <TableCell className="border border-border py-2 text-sm font-medium text-foreground">
                          {second.key}
                        </TableCell>
                        <TableCell className="border border-border py-2 text-sm text-foreground">
                          {second.value}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="border border-border py-2" />
                        <TableCell className="border border-border py-2" />
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </UITable>
        </div>
      )}
    </div>
  );
}

export const TableExpandedRow: React.FC<TableExpandedRowProps> = ({
  colSpan,
  isLoading,
  error,
  series,
  privateTag2F47,
  privateTagFallback,
  className = 'rounded-b-[10px] border-2 border-t-0 border-blue-400 bg-muted text-foreground dark:border-blue-500 overflow-hidden',
}) => {
  return (
    <TableRow className={className}>
      <TableCell colSpan={colSpan} className="bg-muted">
        <div className="flex flex-col gap-0">
          <PrivateTagSection content={privateTag2F47} fallback={privateTagFallback} />
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              Loading series data...
            </div>
          ) : error ? (
            <div className="p-4 text-sm text-destructive">{error}</div>
          ) : series && series.length > 0 ? (
            <SeriesTable series={series} />
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              No series data found.
            </div>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};
